import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useAuthFetch } from '@/hooks/useAuthFetch';
import { HiOutlinePlus, HiOutlineShare, HiOutlineChartBar, HiOutlineDocumentText } from 'react-icons/hi';
import { HiOutlineClipboardDocumentList, HiOutlineClock, HiOutlineCheckCircle, HiOutlineTrash, HiOutlineArrowPath, HiOutlineEnvelope } from 'react-icons/hi2';
import { InviteModal } from '@/components/share/InviteModal';
import type { InterviewStatus } from '@acde/shared';

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface InterviewListItem {
  id: string;
  title: string;
  description?: string;
  status: InterviewStatus;
  questionCount: number;
  responseCount: number;
  shareToken: string;
  createdAt: string;
  updatedAt: string;
}

interface ResponseListItem {
  id: string;
  interviewId: string;
  shareToken?: string;
  interviewTitle: string;
  status: string;
  startedAt: string;
  submittedAt?: string;
  duration?: number;
}

type TabType = 'recent' | 'my-interviews' | 'filled';

const STATUS_STYLES: Record<InterviewStatus, { bg: string; text: string }> = {
  draft: { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-700 dark:text-gray-300' },
  active: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400' },
  closed: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400' },
};

// Human-readable status labels
const formatStatus = (status: string): string => {
  const statusLabels: Record<string, string> = {
    'in_progress': 'In Progress',
    'submitted': 'Submitted',
    'draft': 'Draft',
    'active': 'Active',
    'closed': 'Closed',
    'completed': 'Completed',
    'pending': 'Pending',
  };
  return statusLabels[status] || status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

export function DashboardPage() {
  const navigate = useNavigate();
  const { user, isReady } = useAuth();
  const { authFetch } = useAuthFetch();

  const [activeTab, setActiveTab] = useState<TabType>('my-interviews');
  const [interviews, setInterviews] = useState<InterviewListItem[]>([]);
  const [filledResponses, setFilledResponses] = useState<ResponseListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [inviteInterview, setInviteInterview] = useState<InterviewListItem | null>(null);

  // Fetch interviews created by user
  const fetchInterviews = useCallback(async () => {
    if (!isReady) return;

    try {
      const response = await authFetch(`${API_URL}/interviews`);
      if (response.ok) {
        const data = await response.json();
        setInterviews(data.interviews || []);
      }
    } catch (err) {
      console.error('[Dashboard] Failed to fetch interviews:', err);
    }
  }, [authFetch, isReady]);

  // Fetch responses submitted by user
  const fetchFilledResponses = useCallback(async () => {
    if (!isReady) return;

    try {
      const response = await authFetch(`${API_URL}/responses`);
      if (response.ok) {
        const data = await response.json();
        setFilledResponses(data.responses || []);
      }
    } catch (err) {
      console.error('[Dashboard] Failed to fetch responses:', err);
    }
  }, [authFetch, isReady]);

  // Initial fetch
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setError(null);
      await Promise.all([fetchInterviews(), fetchFilledResponses()]);
      setIsLoading(false);
    };
    loadData();
  }, [fetchInterviews, fetchFilledResponses]);

  // Copy share link
  const handleCopyShareLink = async (token: string) => {
    const shareUrl = `${window.location.origin}/respond/${token}`;
    await navigator.clipboard.writeText(shareUrl);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const handleDeleteResponse = async (response: ResponseListItem) => {
    try {
      const url = `${API_URL}/responses/${response.id}${response.interviewId ? `?interviewId=${response.interviewId}` : ''}`;
      const res = await authFetch(url, { method: 'DELETE' });
      if (res.ok) {
        setFilledResponses((prev) => prev.filter((r) => r.id !== response.id));
      }
    } catch (err) {
      console.error('[Dashboard] Failed to delete response:', err);
    }
  };

  const handleRestartResponse = async (response: ResponseListItem) => {
    try {
      setError(null);
      const res = await authFetch(
        `${API_URL}/responses/${response.id}${response.interviewId ? `?interviewId=${response.interviewId}` : ''}`,
        { method: 'DELETE' },
      );
      if (res.ok) {
        setFilledResponses((prev) => prev.filter((r) => r.id !== response.id));
        if (response.shareToken) {
          navigate(`/respond/${response.shareToken}`);
        }
      } else {
        console.error('[Dashboard] Failed to restart response: non-2xx status', res.status, res.statusText);
        setError('Failed to restart the response. Please try again.');
      }
    } catch (err) {
      console.error('[Dashboard] Failed to restart response:', err);
      setError('Failed to restart the response. Please check your connection and try again.');
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' });
  };

  // Get recent items (combined and sorted)
  const getRecentItems = () => {
    const combined = [
      ...interviews.map((i) => ({ ...i, type: 'interview' as const, sortDate: i.updatedAt })),
      ...filledResponses.map((r) => ({ ...r, type: 'response' as const, sortDate: r.submittedAt || r.startedAt })),
    ];
    return combined
      .sort((a, b) => new Date(b.sortDate).getTime() - new Date(a.sortDate).getTime())
      .slice(0, 10);
  };

  const tabs = [
    { id: 'recent' as const, label: 'Recent', icon: HiOutlineClock },
    { id: 'my-interviews' as const, label: 'My Interviews', icon: HiOutlineClipboardDocumentList },
    { id: 'filled' as const, label: 'Filled Interviews', icon: HiOutlineCheckCircle },
  ];

  return (
    <div className="animate-fade-in max-w-6xl mx-auto px-4 sm:px-0">
      {/* Welcome Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-1 sm:mb-2">
          {getGreeting()}, {user?.name?.split(' ')[0] || 'there'}
        </h1>
        <p className="text-muted text-base sm:text-lg">
          Create AI-powered interviews and collect insights.
        </p>
      </div>

      {/* Create Interview Button */}
      <div className="mb-6 sm:mb-8">
        <button
          onClick={() => navigate('/interviews/new')}
          className="btn btn-primary flex items-center justify-center gap-2 text-base px-6 py-3 w-full sm:w-auto"
        >
          <HiOutlinePlus className="w-5 h-5" aria-hidden="true" />
          Create New Interview
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg" role="alert">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-border mb-6 -mx-4 sm:mx-0">
        <nav className="flex gap-0 sm:gap-1 -mb-px overflow-x-auto hide-scrollbar px-4 sm:px-0" role="tablist" aria-label="Dashboard views">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted hover:text-foreground hover:border-border'
              }`}
            >
              <tab.icon className="w-4 h-4" aria-hidden="true" />
              <span className="hidden sm:inline">{tab.label}</span>
              <span className="sm:hidden">{tab.id === 'my-interviews' ? 'My' : tab.id === 'filled' ? 'Filled' : 'Recent'}</span>
              {tab.id === 'my-interviews' && interviews.length > 0 && (
                <span className="px-1.5 py-0.5 text-xs rounded-full bg-primary/10 text-primary">
                  {interviews.length}
                </span>
              )}
              {tab.id === 'filled' && filledResponses.length > 0 && (
                <span className="px-1.5 py-0.5 text-xs rounded-full bg-primary/10 text-primary">
                  {filledResponses.length}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {isLoading ? (
        <div className="space-y-4" role="status">
          <span className="sr-only">Loading...</span>
          {[1, 2, 3].map((i) => (
            <div key={i} className="card animate-pulse">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-user-msg" />
                <div className="flex-1">
                  <div className="h-5 bg-user-msg rounded w-1/3 mb-2" />
                  <div className="h-4 bg-user-msg rounded w-1/4" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          <span className="sr-only" aria-live="polite">{copiedToken ? "Share link copied to clipboard" : ""}</span>
          {/* Recent Tab */}
          {activeTab === 'recent' && (
            <div className="space-y-3" role="tabpanel">
              {getRecentItems().length === 0 ? (
                <div className="card text-center py-12">
                  <HiOutlineClock className="w-12 h-12 mx-auto text-muted mb-4" aria-hidden="true" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No recent activity</h3>
                  <p className="text-muted mb-4">Create an interview or respond to one to see it here.</p>
                </div>
              ) : (
                getRecentItems().map((item) => (
                  <div 
                    key={item.type === 'interview' ? `i-${item.id}` : `r-${item.id}`} 
                    className="card card-clickable group"
                    onClick={() => {
                      if (item.type === 'interview') {
                        navigate(`/interviews/${item.id}/results`);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        if (item.type === 'interview') {
                          navigate(`/interviews/${item.id}/results`);
                        }
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`${item.type === 'interview' ? (item as InterviewListItem).title : (item as ResponseListItem).interviewTitle} - ${item.type === 'interview' ? (item as InterviewListItem).status : formatStatus((item as ResponseListItem).status)}`}
                  >
                    <div className="flex items-center gap-4">
                      {item.type === 'interview' ? (
                        <>
                          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary/20 to-cyan-500/10 flex items-center justify-center shrink-0">
                            <HiOutlineDocumentText className="w-6 h-6 text-primary" aria-hidden="true" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="font-medium text-foreground truncate max-w-[200px] sm:max-w-none">{(item as InterviewListItem).title}</span>
                              <span className={`px-2 py-0.5 rounded text-xs shrink-0 ${STATUS_STYLES[(item as InterviewListItem).status].bg} ${STATUS_STYLES[(item as InterviewListItem).status].text}`}>
                                {(item as InterviewListItem).status}
                              </span>
                            </div>
                            <div className="text-xs text-muted">
                              {(item as InterviewListItem).responseCount} responses • Updated {formatRelativeTime(item.sortDate)}
                            </div>
                          </div>
                          <svg className="w-5 h-5 text-muted group-hover:text-primary shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </>
                      ) : (
                        <>
                          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-500/20 to-teal-500/10 flex items-center justify-center shrink-0">
                            <HiOutlineCheckCircle className="w-6 h-6 text-emerald-600" aria-hidden="true" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="font-medium text-foreground truncate max-w-[200px] sm:max-w-none">{(item as ResponseListItem).interviewTitle}</span>
                              <span className={`px-2 py-0.5 rounded text-xs shrink-0 ${(item as ResponseListItem).status === 'submitted' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'}`}>
                                {formatStatus((item as ResponseListItem).status)}
                              </span>
                            </div>
                            <div className="text-xs text-muted">
                              Filled • {formatRelativeTime(item.sortDate)}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* My Interviews Tab */}
          {activeTab === 'my-interviews' && (
            <div className="space-y-3" role="tabpanel">
              {interviews.length === 0 ? (
                <div className="card text-center py-12">
                  <HiOutlineClipboardDocumentList className="w-12 h-12 mx-auto text-muted mb-4" aria-hidden="true" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No interviews yet</h3>
                  <p className="text-muted mb-4">Create your first AI-powered interview to get started.</p>
                  <button onClick={() => navigate('/interviews/new')} className="btn btn-primary">
                    Create Interview
                  </button>
                </div>
              ) : (
                interviews.map((interview) => (
                  <div 
                    key={interview.id} 
                    className="card card-clickable"
                    onClick={() => navigate(`/interviews/${interview.id}/results`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/interviews/${interview.id}/results`);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`${interview.title} - ${interview.status}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                      {/* Main content area - fully clickable */}
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary/20 to-cyan-500/10 flex items-center justify-center shrink-0">
                          <HiOutlineDocumentText className="w-6 h-6 text-primary" aria-hidden="true" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="font-medium text-foreground truncate max-w-[200px] sm:max-w-none">{interview.title}</span>
                            <span className={`px-2 py-0.5 rounded text-xs shrink-0 ${STATUS_STYLES[interview.status].bg} ${STATUS_STYLES[interview.status].text}`}>
                              {interview.status}
                            </span>
                          </div>
                          <div className="text-xs text-muted flex items-center gap-1 sm:gap-2 flex-wrap">
                            <span>{interview.questionCount} questions</span>
                            <span className="hidden sm:inline">•</span>
                            <span>{interview.responseCount} responses</span>
                            <span className="hidden sm:inline">•</span>
                            <span className="hidden sm:inline">Updated {formatRelativeTime(interview.updatedAt)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="flex items-center gap-2 shrink-0 sm:ml-4 pt-3 sm:pt-0 border-t sm:border-t-0 border-border">
                        {/* Share button */}
                        {interview.status === 'active' && (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setInviteInterview(interview);
                              }}
                              className="icon-btn"
                              title="Invite people"
                              aria-label="Invite people"
                            >
                              <HiOutlineEnvelope className="w-5 h-5" aria-hidden="true" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyShareLink(interview.shareToken);
                              }}
                              className="icon-btn"
                              title="Copy share link"
                              aria-label="Copy share link"
                            >
                              {copiedToken === interview.shareToken ? (
                                <HiOutlineCheckCircle className="w-5 h-5 text-green-500" aria-hidden="true" />
                              ) : (
                                <HiOutlineShare className="w-5 h-5" aria-hidden="true" />
                              )}
                            </button>
                          </>
                        )}

                        {/* Results button */}
                        <Link
                          to={`/interviews/${interview.id}/results`}
                          className="icon-btn"
                          title="View results"
                          aria-label="View results"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <HiOutlineChartBar className="w-5 h-5" aria-hidden="true" />
                        </Link>

                        {/* Edit button */}
                        <Link
                          to={`/interviews/${interview.id}/edit`}
                          className="icon-btn"
                          title="Edit interview"
                          aria-label="Edit interview"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </Link>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Filled Interviews Tab */}
          {activeTab === 'filled' && (
            <div className="space-y-3" role="tabpanel">
              {filledResponses.length === 0 ? (
                <div className="card text-center py-12">
                  <HiOutlineCheckCircle className="w-12 h-12 mx-auto text-muted mb-4" aria-hidden="true" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No filled interviews</h3>
                  <p className="text-muted">Interviews you respond to will appear here.</p>
                </div>
              ) : (
                filledResponses.map((response) => (
                  <div key={response.id} className="card">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-500/20 to-teal-500/10 flex items-center justify-center">
                        <HiOutlineCheckCircle className="w-6 h-6 text-emerald-600" aria-hidden="true" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-foreground truncate">{response.interviewTitle}</span>
                          <span className={`px-2 py-0.5 rounded text-xs ${response.status === 'submitted' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'}`}>
                            {formatStatus(response.status)}
                          </span>
                        </div>
                        <div className="text-xs text-muted flex items-center gap-2">
                          {response.submittedAt ? (
                            <>
                              <span>Submitted {formatRelativeTime(response.submittedAt)}</span>
                              {response.duration && (
                                <>
                                  <span>•</span>
                                  <span>{Math.floor(response.duration / 60)}m {response.duration % 60}s</span>
                                </>
                              )}
                            </>
                          ) : (
                            <span>Started {formatRelativeTime(response.startedAt)}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {response.status === 'in_progress' && (
                          <>
                            {response.shareToken && (
                              <Link
                                to={`/respond/${response.shareToken}`}
                                className="btn btn-primary text-sm"
                              >
                                Continue
                              </Link>
                            )}
                            {response.shareToken && (
                              <button
                                onClick={() => handleRestartResponse(response)}
                                className="icon-btn"
                                title="Restart interview"
                                aria-label="Restart interview"
                              >
                                <HiOutlineArrowPath className="w-5 h-5" aria-hidden="true" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteResponse(response)}
                              className="icon-btn text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                              title="Delete response"
                              aria-label="Delete response"
                            >
                              <HiOutlineTrash className="w-5 h-5" aria-hidden="true" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}

      {/* Invite Modal */}
      {inviteInterview && (
        <InviteModal
          isOpen={!!inviteInterview}
          onClose={() => setInviteInterview(null)}
          interviewId={inviteInterview.id}
          interviewTitle={inviteInterview.title}
          shareToken={inviteInterview.shareToken}
        />
      )}

      {/* Quick stats (only show if user has interviews) */}
      {interviews.length > 0 && (
        <div className="mt-8 grid grid-cols-3 gap-2 sm:gap-4">
          <div className="card bg-gradient-to-br from-primary/5 to-transparent text-center sm:text-left">
            <div className="text-2xl sm:text-3xl font-bold text-primary">{interviews.length}</div>
            <div className="text-xs sm:text-sm text-muted">Total Interviews</div>
          </div>
          <div className="card bg-gradient-to-br from-green-500/5 to-transparent text-center sm:text-left">
            <div className="text-2xl sm:text-3xl font-bold text-green-600">{interviews.filter((i) => i.status === 'active').length}</div>
            <div className="text-xs sm:text-sm text-muted">Active</div>
          </div>
          <div className="card bg-gradient-to-br from-purple-500/5 to-transparent text-center sm:text-left">
            <div className="text-2xl sm:text-3xl font-bold text-purple-600">{interviews.reduce((sum, i) => sum + i.responseCount, 0)}</div>
            <div className="text-xs sm:text-sm text-muted">Responses</div>
          </div>
        </div>
      )}
    </div>
  );
}
